import { useMemo } from 'react';
import { useArbiterClient } from '../context/ArbiterClientContext';
import { usePaginatedApi } from './usePaginatedApi';
import type { EvaluationSummary, ListEvaluationsInput } from '@amzn/helianthus-arbiter-client';

interface UseEvaluationsResult {
  evaluations: EvaluationSummary[];
  loading: boolean;
  fetchingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

interface UseEvaluationsOptions {
  /**
   * Pages to request per load. Required in practice: the evaluations table holds over a million
   * rows in prod, so an unbounded drain issues thousands of ListEvaluations calls for a page that
   * displays 50 of them. Callers page on with `loadMore()`.
   */
  maxPages: number;
}

export function useEvaluations(
  params: Omit<ListEvaluationsInput, 'continuationToken'> = {},
  options: UseEvaluationsOptions,
): UseEvaluationsResult {
  const client = useArbiterClient();

  // Stabilize params to prevent usePaginatedApi refetch on every render.
  // sortOrder defaults to DESC so the first page holds the most recent evaluations, which is what
  // makes a bounded number of pages a useful answer rather than an arbitrary slice. A caller that
  // passes ASC is asking the service for the other end of the table, not reordering what it got.
  const stableParams = useMemo(
    () => ({
      resourceType: params.resourceType,
      resourceId: params.resourceId,
      bindleLock: params.bindleLock,
      completed: params.completed,
      sortOrder: params.sortOrder ?? ('DESC' as const),
      limit: params.limit,
    }),
    [params.resourceType, params.resourceId, params.bindleLock, params.completed, params.sortOrder, params.limit],
  );

  const { data, loading, fetchingMore, error, hasMore, loadMore, refetch } = usePaginatedApi(
    (fetchParams) =>
      client.listEvaluations(fetchParams).then((r) => ({
        items: r.evaluations ?? [],
        continuationToken: r.continuationToken,
      })),
    stableParams,
    { maxPages: options.maxPages },
  );

  return { evaluations: data, loading, fetchingMore, error, hasMore, loadMore, refetch };
}
